import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { stringNotEmpty } from 'Util/TypeGuards';
/* eslint-disable max-len */

export interface IAdditionalBookingOptionEntityAttributes extends IModelAttributes {
	name: string;
	abbreviation: string;
	information: string;
	order: number;
	staticPrice: number;
	removable: boolean;
	excludeGST: boolean;
	canExportReport: boolean;
	showInCheckIn: boolean;
	removalTitle: string;
	removalInformation: string;
	isSingular: boolean;
	disabled: boolean;
	isRestricted: boolean;
	vehicleAddOn: boolean;
	colour: string;
	emailTemplate: string;
	iconName?: string;
	passengerAddOn: boolean;
	freightAddOn: boolean;

	addOnsPrices: Array<Models.AddOnsPriceEntity | Models.IAddOnsPriceEntityAttributes>;
	additionalBookingSectionId: string;
	additionalBookingSection: Models.AdditionalBookingSectionEntity | Models.IAdditionalBookingSectionEntityAttributes;
	tickets: Array<Models.TicketEntity | Models.ITicketEntityAttributes>;
	displayInfoInTooltip?: boolean;
	isInitialBookingOnly?: boolean;
	showInManifest: boolean;
	capacityTypes: Array<Models.CapacityTypeEntity | Models.ICapacityTypeEntityAttributes>;
}
@entity('AdditionalBookingOptionEntity', 'Additional Booking Option')
export default class AdditionalBookingOptionEntity extends Model implements IAdditionalBookingOptionEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<AdditionalBookingOptionEntity, string>()
	public name: string;

	@observable
	@attribute<AdditionalBookingOptionEntity, string>()
	public abbreviation: string;

	@observable
	@attribute<AdditionalBookingOptionEntity, string>()
	public information: string;

	@observable
	@attribute<AdditionalBookingOptionEntity, number>()
	public order: number;

	@observable
	@attribute<AdditionalBookingOptionEntity, number>()
	public staticPrice: number;

	/**
	 * Indicates whether this option can be removed when altering a booking.
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public removable: boolean = false;

	/**
	 * When true, this item will not be included in GST calculations.
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public excludeGST: boolean = false;

	/**
	 * Whether this additional booking option can be exported or not.
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public canExportReport: boolean = false;

	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public showInCheckIn: boolean;

	/**
	 * This is the message displayed when altering an additional booking option on a booking.
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, string>()
	public removalTitle: string;

	@observable
	@attribute<AdditionalBookingOptionEntity, string>()
	public removalInformation: string;

	/**
	 * This is set to true if only a single item can be added to a booking
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public isSingular: boolean;

	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public isRestricted: boolean;

	/**
	 * Whether or not the add on will show for vehicle bookings.
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public vehicleAddOn: boolean;

	/**
	 * Hex value
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, string>()
	public colour: string;

	@observable
	@attribute<AdditionalBookingOptionEntity, string>()
	public emailTemplate: string;

	@observable
	@attribute<AdditionalBookingOptionEntity, string>()
	public iconName?: string;

	@observable
	public icon: Blob;

	/**
	 * Whether or not the add on will show for passenger bookings.
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public passengerAddOn: boolean;

	/**
	 * Whether or not the add on will show for freight bookings.
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public freightAddOn: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public addOnsPrices: Models.AddOnsPriceEntity[] = [];

	@observable
	@attribute()
	public additionalBookingSectionId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public additionalBookingSection: Models.AdditionalBookingSectionEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public tickets: Models.TicketEntity[] = [];

	/**
	 * Indicates whether the information about add on is displayed in a tooltip or displayed below the title.
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public displayInfoInTooltip?: boolean;

	/**
	 * Indicates whether the add-on can only be added to the booking during the initial reservation process, or can be added during modification.
	 */
	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public isInitialBookingOnly?: boolean;

	@observable
	@attribute<AdditionalBookingOptionEntity, boolean>()
	public showInManifest: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public capacityTypes: Models.CapacityTypeEntity[] = [];

	constructor(attributes?: Partial<IAdditionalBookingOptionEntityAttributes>) {
		super(attributes);

		this.addOnsPrices = [];
		this.tickets = [];
		this.capacityTypes = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAdditionalBookingOptionEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.abbreviation !== undefined) {
				this.abbreviation = attributes.abbreviation;
			}
			if (attributes.information !== undefined) {
				this.information = attributes.information;
			}
			if (attributes.order !== undefined) {
				this.order = attributes.order;
			}
			if (attributes.staticPrice !== undefined) {
				this.staticPrice = attributes.staticPrice;
			}
			if (attributes.removable !== undefined) {
				this.removable = attributes.removable;
			}
			if (attributes.excludeGST !== undefined) {
				this.excludeGST = attributes.excludeGST;
			}
			if (attributes.canExportReport !== undefined) {
				this.canExportReport = attributes.canExportReport;
			}
			if (attributes.showInCheckIn !== undefined) {
				this.showInCheckIn = attributes.showInCheckIn;
			}
			if (attributes.removalTitle !== undefined) {
				this.removalTitle = attributes.removalTitle;
			}
			if (attributes.removalInformation !== undefined) {
				this.removalInformation = attributes.removalInformation;
			}
			if (attributes.isSingular !== undefined) {
				this.isSingular = attributes.isSingular;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.isRestricted !== undefined) {
				this.isRestricted = attributes.isRestricted;
			}
			if (attributes.vehicleAddOn !== undefined) {
				this.vehicleAddOn = attributes.vehicleAddOn;
			}
			if (attributes.colour !== undefined) {
				this.colour = attributes.colour;
			}
			if (attributes.emailTemplate !== undefined) {
				this.emailTemplate = attributes.emailTemplate;
			}
			if (stringNotEmpty(attributes.iconName)) {
				this.iconName = attributes.iconName;
			}
			if (attributes.passengerAddOn !== undefined) {
				this.passengerAddOn = attributes.passengerAddOn;
			}
			if (attributes.freightAddOn !== undefined) {
				this.freightAddOn = attributes.freightAddOn;
			}
			if (attributes.addOnsPrices !== undefined && Array.isArray(attributes.addOnsPrices)) {
				if (!this.addOnsPrices) {
					this.addOnsPrices = [];
				}
				for (const model of attributes.addOnsPrices) {
					if (model instanceof Models.AddOnsPriceEntity) {
						this.addOnsPrices.push(model);
					} else {
						this.addOnsPrices.push(new Models.AddOnsPriceEntity(model));
					}
				}
			}
			if (attributes.additionalBookingSectionId !== undefined) {
				this.additionalBookingSectionId = attributes.additionalBookingSectionId;
			}
			if (attributes.additionalBookingSection !== undefined) {
				if (attributes.additionalBookingSection === null) {
					this.additionalBookingSection = attributes.additionalBookingSection;
				} else if (attributes.additionalBookingSection instanceof Models.AdditionalBookingSectionEntity) {
					this.additionalBookingSection = attributes.additionalBookingSection;
					this.additionalBookingSectionId = attributes.additionalBookingSection.id;
				} else {
					this.additionalBookingSection = new Models.AdditionalBookingSectionEntity(
						attributes.additionalBookingSection,
					);
					this.additionalBookingSectionId = this.additionalBookingSection.id;
				}
			}
			if (attributes.tickets !== undefined && Array.isArray(attributes.tickets)) {
				if (!this.tickets) {
					this.tickets = [];
				}
				for (const model of attributes.tickets) {
					if (model instanceof Models.TicketEntity) {
						this.tickets.push(model);
					} else {
						this.tickets.push(new Models.TicketEntity(model));
					}
				}
			}
			if (attributes.displayInfoInTooltip !== undefined) {
				this.displayInfoInTooltip = attributes.displayInfoInTooltip;
			}
			if (attributes.isInitialBookingOnly !== undefined) {
				this.isInitialBookingOnly = attributes.isInitialBookingOnly;
			}
			if (attributes.showInManifest !== undefined) {
				this.showInManifest = attributes.showInManifest;
			}
			if (attributes.capacityTypes !== undefined && Array.isArray(attributes.capacityTypes)) {
				if (!this.capacityTypes) {
					this.capacityTypes = [];
				}
				for (const model of attributes.capacityTypes) {
					if (model instanceof Models.CapacityTypeEntity) {
						this.capacityTypes.push(model);
					} else {
						this.capacityTypes.push(new Models.CapacityTypeEntity(model));
					}
				}
			}
		}
	}
}
